import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  BookOpen,
  Compass,
  Lock,
  Moon,
  Radio,
  Sun,
  Wallet as WalletIcon,
} from 'lucide-react';

import { LoginArea } from '@/components/auth/LoginArea';
import { Foundations } from '@/components/ground/Foundations';
import { GroundFeed } from '@/components/ground/GroundFeed';
import { GroundProvider } from '@/components/ground/GroundProvider';
import { Protocols } from '@/components/ground/Protocols';
import { Wallet } from '@/components/ground/Wallet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGround } from '@/hooks/useGround';
import { useTheme } from '@/hooks/useTheme';

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  // Default to the dark presentation; only an explicit light choice is light.
  const isDark = theme !== 'light';

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </Button>
  );
}

function GroundApp() {
  const [tab, setTab] = useState<string>('foundations');
  const [protocolFocus, setProtocolFocus] = useState<string | null>(null);
  const { stats, completedDays, foundationsComplete } = useGround();

  const openProtocols = (protocolId?: string) => {
    setProtocolFocus(protocolId ?? null);
    setTab('protocols');
  };

  const tabs = [
    { value: 'foundations', label: 'Foundations', icon: Compass },
    { value: 'protocols', label: 'Protocols', icon: BookOpen, locked: !foundationsComplete },
    { value: 'feed', label: 'Feed', icon: Radio },
    { value: 'wallet', label: 'Wallet', icon: WalletIcon },
  ];

  return (
    <div className="relative min-h-screen">
      <div className="pointer-events-none fixed inset-0 bg-grain opacity-40" aria-hidden />
      <div
        className="pointer-events-none fixed inset-x-0 top-0 h-72 bg-gradient-to-b from-primary/10 to-transparent"
        aria-hidden
      />

      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <img src="/ground.svg" alt="" className="size-9 rounded-lg" />
          <div className="leading-tight">
            <div className="font-display text-lg font-semibold">Ground</div>
            <div className="hidden text-[11px] text-muted-foreground sm:block">
              Foundations, Protocols &amp; Feed
            </div>
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <div className="mr-1 hidden items-center gap-3 text-xs text-muted-foreground md:flex">
              <span>
                <span className="font-medium text-foreground tabular-nums">
                  {completedDays.length}/5
                </span>{' '}
                days
              </span>
              <span className="h-3 w-px bg-border" />
              <span>
                <span className="font-medium text-foreground tabular-nums">{stats.sessions}</span>{' '}
                sessions
              </span>
              <span className="h-3 w-px bg-border" />
              <span>
                <span className="font-medium text-foreground tabular-nums">
                  {stats.satsEarned.toLocaleString()}
                </span>{' '}
                sats
              </span>
            </div>
            <ThemeToggle />
            <div className="hidden sm:block">
              <LoginArea className="max-w-40" />
            </div>
          </div>
        </div>
      </header>

      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (value === 'protocols') setProtocolFocus(null);
          setTab(value);
        }}
        className="relative"
      >
        <div className="sticky top-14 z-30 border-b border-border/70 bg-background/80 backdrop-blur-md">
          <div className="mx-auto max-w-5xl px-4">
            <TabsList
              variant="line"
              style={{ height: 'auto' }}
              className="w-full justify-start gap-1 rounded-none bg-transparent p-0"
            >
              {tabs.map(({ value, label, icon: Icon, locked }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="h-11 flex-none gap-2 rounded-none border-b-2 border-transparent bg-transparent px-3 after:hidden data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none sm:px-4"
                >
                  <Icon className="size-4" />
                  <span className="hidden sm:inline">{label}</span>
                  {locked && <Lock className="size-3 text-muted-foreground" />}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        <main className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
          <TabsContent value="foundations">
            <Foundations onOpenProtocols={openProtocols} />
          </TabsContent>
          <TabsContent value="protocols">
            <Protocols focusProtocolId={protocolFocus} />
          </TabsContent>
          <TabsContent value="feed">
            <GroundFeed />
          </TabsContent>
          <TabsContent value="wallet">
            <Wallet />
          </TabsContent>
        </main>
      </Tabs>

      <footer className="relative border-t border-border/70">
        <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-3 px-4 py-8 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <p>Ground — somatic, movement, digital, nature, and rest practices on Nostr.</p>
          <p>
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noreferrer noopener"
              className="text-foreground underline underline-offset-2"
            >
              Shakespeare
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

const Index = () => {
  useSeoMeta({
    title: 'Ground — Foundations, Protocols & Feed',
    description:
      'A free five-day Foundations protocol introducing somatic, movement, digital, nature, and rest practices — plus deeper Protocols, a Nostr feed, and a Lightning wallet.',
  });

  return (
    <GroundProvider>
      <GroundApp />
    </GroundProvider>
  );
};

export default Index;
